import { PivotItem } from "@fluentui/react";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import { TabsContainer } from "../../../../SharedComponents/TabsContainer";
import { useGetUIEnablementsSettingsQuery } from "../../../../slices/Home.slice";
import { useGetSupportDataSummaryByProductIdMutation } from "../../../../slices/ExploreProducts.slice";
import { buildDisabledFeatureLookup } from "../../../../Utils/js-common";
import { PRODUCT_VIEW_DATA } from "../..//Common/ProductView.constants";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";

export const SupportTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get("section") || "Summary";
  const [enableTabs, setEnableTabs] = useState<boolean>(true);
  const {
    pivot: { ariaLabel },
    pivotItems
  } = PRODUCT_VIEW_DATA.TABS.TAB.SUPPORT_VIEW.TABS_DATA;
  const uiEnablementData = useGetUIEnablementsSettingsQuery(undefined)?.data || [];
  const disabled = buildDisabledFeatureLookup(uiEnablementData);
  const disabledTabOptions = disabled["subTab"] || [];
  const filteredTabOptions = pivotItems.filter(
    ({ headerText }) =>
      !disabledTabOptions
        .filter(({ feature, tab }) => feature === "Explore Products" && tab === "Support")
        .map(({ subTab }) => subTab)
        .includes(headerText)
  );
  const selectedTabKey = filteredTabOptions.filter((tab) => tab.headerText === selectedTab)[0]?.itemKey;
  const renderTabs = filteredTabOptions.map(({ headerText, itemKey, component }, index) => (
    <PivotItem headerText={headerText} itemKey={itemKey} key={`${itemKey + index}`}>
      {component()}
    </PivotItem>
  ));
  const filters = useContext(ProductViewFiltersContext);
  const [getData, { data: supportData, error, isError, isLoading, isSuccess }] =
    useGetSupportDataSummaryByProductIdMutation({
      fixedCacheKey: "getSupportData"
    });

  // Events
  const handleSelectedTab = (item?: PivotItem) => {
    if (item?.props.headerText) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("section", item.props.headerText || "");
        return newParams;
      });
    }
  };

  // Hooks
  useEffect(() => {
    if (filters.productId) {
      getData({
        hasPartner: filters.hasPartner,
        industry: filters.industry,
        isCAT: filters.isCAT,
        isFastTrackStrategic: filters.isFastTrackStrategic,
        isSST: filters.isSST,
        productId: filters.productId,
        region: filters.region,
        segmentGroup: filters.segmentGroup,
        segment: filters.segment,
        subSegment: filters.subSegment
      });
    }
  }, [
    filters.hasPartner,
    filters.industry,
    filters.isCAT,
    filters.isFastTrackStrategic,
    filters.isSST,
    filters.productId,
    filters.region,
    filters.segmentGroup,
    filters.segment,
    filters.subSegment,
    getData
  ]);

  useEffect(() => {
    if (isSuccess) {
      setEnableTabs(
        Boolean(
          supportData?.serviceRequesOpentState?.data?.length ||
          supportData?.volumeTabSRSupportData?.data?.length ||
          supportData?.volumeTabCRISupportData?.data?.length ||
          supportData?.qualityTabSupportData?.data?.length
        )
      );
    }
  }, [isSuccess]);

  if (!isLoading && isError) {
    return <DisplayAPIsException error={error} height={250} />;
  }

  return (
    <div>
      <div>
        {(isLoading || enableTabs) && (
          <TabsContainer ariaLabel={ariaLabel} selectedKey={selectedTabKey} handleSelectedTab={handleSelectedTab}>
            {renderTabs}
          </TabsContainer>
        )}
        {isSuccess && !enableTabs && <NoDataDisplayMessage productName={filters.productName} />}
      </div>
    </div>
  );
};

const NoDataDisplayMessage = ({ productName }: { productName?: string }) => (
  <div className={PRODUCT_VIEW_STYLES.TAB.FEATURE_VIEW.CLASS_NAMES.noDataForProductDisplayMessage}>
    {productName} has no Support data available yet.
  </div>
);
